import { MouseEvent as ReactMouseEvent, ReactNode, useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'

import { isSpaPath } from '../../lib/spaPath'
import { Button, ButtonVariant } from './Button'

function _isSpaLinkClick(event: ReactMouseEvent): boolean {
  const anchor = event.target instanceof Element ? event.target.closest('a[href]') : null
  return anchor instanceof HTMLAnchorElement && isSpaPath(new URL(anchor.href))
}

function _useCloseOnNavigation(close: () => void) {
  const { key } = useLocation()
  useEffect(close, [key])
}

function _useCloseOnOutsideClick(open: boolean, close: () => void) {
  const ref = useRef<HTMLDivElement & HTMLLIElement>(null)

  useEffect(() => {
    if (!open) {
      return
    }
    const handleClick = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        close()
      }
    }
    document.addEventListener('click', handleClick)
    return () => document.removeEventListener('click', handleClick)
  }, [open, close])

  return ref
}

export function DropdownLi({
  className = 'dropdown',
  toggle,
  children,
}: {
  className?: string
  toggle: ReactNode
  children: ReactNode
}) {
  const [open, setOpen] = useState(false)
  const ref = _useCloseOnOutsideClick(open, () => setOpen(false))
  _useCloseOnNavigation(() => setOpen(false))

  return (
    <li ref={ref} className={`${className}${open ? ' open' : ''}`}>
      <a
        className="dropdown-toggle"
        href="#"
        role="button"
        aria-expanded={open}
        onClick={(event) => {
          event.preventDefault()
          setOpen(!open)
        }}
      >
        {toggle}
      </a>
      <ul
        className="dropdown-menu"
        onClick={(event) => {
          if (!_isSpaLinkClick(event)) {
            setOpen(false)
          }
        }}
      >
        {children}
      </ul>
    </li>
  )
}

export function Dropdown({
  label,
  variant = 'primary',
  raised = false,
  buttonClassName = '',
  buttonStyle,
  className = '',
  style,
  showCaret = true,
  disabled = false,
  children,
}: {
  label: ReactNode
  variant?: ButtonVariant
  raised?: boolean
  buttonClassName?: string
  buttonStyle?: React.CSSProperties
  className?: string
  style?: React.CSSProperties
  showCaret?: boolean
  disabled?: boolean
  children: ReactNode
}) {
  const [open, setOpen] = useState(false)
  const ref = _useCloseOnOutsideClick(open, () => setOpen(false))
  _useCloseOnNavigation(() => setOpen(false))

  return (
    <div ref={ref} className={`btn-group${open ? ' open' : ''} ${className}`.trim()} style={style}>
      <Button
        variant={variant}
        raised={raised}
        className={`dropdown-toggle ${buttonClassName}`.trim()}
        style={buttonStyle}
        aria-expanded={open}
        aria-haspopup="true"
        disabled={disabled}
        onClick={() => setOpen(!open)}
      >
        {label}
        {showCaret && (
          <>
            {' '}
            <b className="caret" />
          </>
        )}
      </Button>
      <ul
        className="dropdown-menu"
        onClick={(event) => {
          if (!_isSpaLinkClick(event)) {
            setOpen(false)
          }
        }}
      >
        {children}
      </ul>
    </div>
  )
}
